import { Component, ViewChild } from '@angular/core';
import { Platform, AlertController, ModalController, NavController } from '@ionic/angular';
import { AuthService } from './core/services/auth.service';
import { TransactionService } from './core/services/transaction.service';
import { AppService } from './core/services/app.service';
import { NavigationEnd, Router } from '@angular/router';
import { EMPTY, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { PointsService } from './core/services/points.service';
import { UserService } from './core/services/user.service';
import { UserProfile } from './core/models/user.modal';
import { map } from 'rxjs/operators';
import { register } from 'swiper/element/bundle';
import { VerifyPinPage } from './shared/modals/verify-pin/verify-pin.page';
import { trigger, transition, style, animate } from '@angular/animations';
import { SpecialReferralOfferHintPage } from './shared/modals/special-referral-offer-hint/special-referral-offer-hint.page';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  animations: [
    trigger('slideInOutCC', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 0 }),
        animate('.7s', style({ transform: 'translateY(0%)', opacity: 1 }))
      ]),
      transition(':leave', [
        style({ transform: 'translateY(0%)', opacity: 1 }),
        animate('.7s', style({ transform: 'translateY(-100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class AppComponent {
  constructor(
    private platform: Platform,
    private authService: AuthService,
    private transactionService: TransactionService,
    private appService: AppService,
    private router: Router,
    private alertController: AlertController,
    private translateService: TranslateService,
    private pointsService: PointsService,
    private modalController: ModalController,
    private navController: NavController,
    private userService: UserService,
  ) {
    register();
    this.initializeApp();
  }

  subscription = new Subscription();
  subscriptionCount = 0;
  @ViewChild('iosDownloadLink') iosDownloadLink;
  @ViewChild('androidDownloadLink') androidDownloadLink;
  showingForceUpdate = false;
  offlineToast: HTMLIonToastElement;
  machineId: string = null;
  myPoints$ = this.pointsService.getMyPoints().pipe(map(res => {
    this.hasSigninToday = res.hasSigninToday;
    return res;
  }));
  isDailyBonusSuccessModalOpen = false;
  dailyBonusPoints = 0;
  hasSigninToday = false;
  isLoggedIn = false;
  currentUser: UserProfile = null;
  lastShownTime: Date;
  showingVerifyPinModal = false;
  showDailyCheckInPoints = false;

  pages = [
    { title: 'sidenav.rewards', url: '/my-points', icon: 'assets/images/icon-rewards.svg' },
    { title: 'sidenav.promotions', url: '/promotions', icon: 'assets/images/icon-promotions.svg' },
    { title: 'sidenav.history', url: '/history', icon: 'assets/images/icon-history.svg' },
    { title: 'sidenav.notifications', url: '/my-messages', icon: 'assets/images/icon-notifications.svg' },
    { title: 'sidenav.profile', url: '/account', icon: 'assets/images/icon-profile.svg' },
  ];

  triggerDailyCheckInPoints() {
    this.showDailyCheckInPoints = true;
    setTimeout(() => this.showDailyCheckInPoints = false, 1000);
  }

  signout() {
    this.authService.logout().subscribe(() => {
      this.isLoggedIn = false;
      this.transactionService.unreadMessageCount$.next(0);
      this.appService.traceAction({ action: 'logout' }).subscribe();
      this.navController.navigateRoot('/login');
    });
  }

  public setDailyBonusSuccessModalOpen(open: boolean) {
    this.isDailyBonusSuccessModalOpen = open;
  }

  public dailyCheckin() {
    this.subscription.add(this.pointsService.dailyCheckIn().subscribe(res => {
      if (res.points) {
        this.dailyBonusPoints = res.points;
        this.setDailyBonusSuccessModalOpen(res.points > 30);
        this.triggerDailyCheckInPoints();
        this.refreshDailyCheckInStatus();
      }
    }));
  }

  private refreshDailyCheckInStatus() {
    this.myPoints$ = this.pointsService.getMyPoints().pipe(map(res => {
      this.hasSigninToday = res.hasSigninToday;
      return res;
    }));
  }

  initializeApp() {
    this.platform.ready().then(() => {

      // 跳转到移动端
      if (this.platform.width() <= 768) {
        window.location.replace(environment.mobileUrl);
        return;
      }

      this.authService.isAuthenticated.asObservable().subscribe(isLoggedIn => {
        this.isLoggedIn = isLoggedIn;
      });

      this.userService.userProfile$.asObservable().subscribe(userProfile => {
        this.currentUser = userProfile;
      });

      this.subscription.add(this.pointsService.refreshPoints$.subscribe(refresh => {
        if (refresh) {
          this.refreshDailyCheckInStatus();
        }
      }));

      // trace page navigation event
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          this.subscription.add(
            this.appService
              .traceAction({
                action: 'page view',
                pageName: event.urlAfterRedirects,
              })
              .subscribe()
          );
          if (event.urlAfterRedirects === '/history' || event.urlAfterRedirects.startsWith('/transaction-details')) {
            if (!this.showingVerifyPinModal) {
              const current = new Date();
              if (this.lastShownTime && ((current.getTime() - this.lastShownTime.getTime()) / 1000 < (60 * 1))) {
                this.transactionService.historyRecord$ = this.transactionService.getTransactionHistory();
                return;
              }
              const userProfile = this.userService.userProfile$.getValue();
              if (userProfile && userProfile.user && !userProfile.user.clastname) {
                this.showUpdatePersonalInfoAlert();
              } else {
                this.showVerifyPin();
              }
            }
          }
        }
      });

      // refresh token if user's token is still valid
      this.authService.validateAuthToken().subscribe((isValid) => {
        if (isValid) {
          this.authService.refreshToken().subscribe(res => {
            if (res.pendingETransfer) {
              this.showPendingETransferPaymentAlert(res.pendingETransfer);
            }
          });
        }
      });

      // re-retrive app info
      this.appService
        .getAppInfo()
        .subscribe((res) => {
          this.appService.appData = res;
        });

      this.transactionService.triggerSpecialReferralOfferHint$.subscribe(triggered => {
        console.log(triggered);
        if (triggered) {
          this.showSpecialReferralOfferHint();
        }
      });
    });
  }

  private async showSpecialReferralOfferHint() {
    const modal = await this.modalController.create({
      component: SpecialReferralOfferHintPage,
      cssClass: 'auto-height special-referral-offer-hint',
      backdropDismiss: true,
    });

    modal.present();
  }

  private async showPendingETransferPaymentAlert(transactionId: string) {
    if (!transactionId) {
      return;
    }
    const alert = await this.alertController.create({
      header: this.translateService.instant('message.pending.payment'),
      message: this.translateService.instant('message.pending.etransfer'),
      buttons: [
        {
          text: this.translateService.instant('tabs.history.paymentDetails'),
          handler: () => {
            this.router.navigate(['/history/view-etransfer-code'], { queryParams: { txn: transactionId } });
          }
        }
      ]
    });

    return await alert.present();
  }

  private async showUpdatePersonalInfoAlert() {
    const alert = await this.alertController.create({
      message: this.translateService.instant('message.missing.personal.info'),
      keyboardClose: true,
      backdropDismiss: false,
      cssClass: 'simple-alert',
      buttons: [
        {
          text: this.translateService.instant('common.cancel'),
          handler: () => {
            this.navController.back();
          }
        },
        {
          text: this.translateService.instant('common.update'),
          handler: () => {
            this.navController.navigateForward('signup-more');
          },
        },
      ],
    });

    return await alert.present();
  }

  private async showVerifyPin() {
    this.showingVerifyPinModal = true;
    this.transactionService.historyRecord$ = EMPTY;
    const modal = await this.modalController.create({
      component: VerifyPinPage,
      componentProps: { from: 'history' },
      cssClass: 'verify-pin',
      backdropDismiss: false,
    });

    modal.onDidDismiss().then((res) => {
      this.showingVerifyPinModal = false;
      if (res.data && res.data.verified) {
        this.lastShownTime = new Date();
        this.transactionService.historyRecord$ = this.transactionService.getTransactionHistory();
      } else if (res.data && res.data.forgotPin) {

      } else {
        this.navController.back();
      }
    });

    modal.present();
  }
}
